import is from '@sindresorhus/is';
import { useIsPresent } from 'framer-motion';
import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { TILE_EXIT_VARIANT } from './constants';
import { useTilingEngineContext } from './TilingEngineContext';

export const useTileExitAnimation = <Element extends HTMLElement>(
    tileId: string,
    nodeRef: RefObject<Element | null>,
): {
    readonly frozenWidth: number | null;
    readonly isPresent: boolean;
    readonly onAnimationComplete: (definition: unknown) => void;
} => {
    const { notify } = useTilingEngineContext();
    const isPresent = useIsPresent();
    const wasPresent = useRef(true);
    const [frozenWidth, setFrozenWidth] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (isPresent) {
            setFrozenWidth(null);
        } else if (wasPresent.current) {
            const measuredWidth = nodeRef.current?.getBoundingClientRect().width;

            notify({ type: 'tileCloseStart', tileId });

            if (is.number(measuredWidth) && measuredWidth > 0) {
                setFrozenWidth(measuredWidth);
            }
        }

        wasPresent.current = isPresent;
    }, [isPresent, nodeRef, notify, tileId]);

    const onAnimationComplete = useCallback(
        (definition: unknown) => {
            if (!isPresent && definition === TILE_EXIT_VARIANT) {
                notify({ type: 'tileCloseComplete', tileId });
            }
        },
        [isPresent, notify, tileId],
    );

    return { frozenWidth, isPresent, onAnimationComplete };
};
